import { api } from './api';
import type {
  ErrorMaster,
  ErrorMasterCreateRequest,
  ErrorMasterUpdateRequest,
  ErrorMasterSearchParams,
  ErrorMasterSearchResponse,
} from '../types';

/** What blocks deleting an error master (notifications referencing its errorCode). */
export interface ErrorMasterDependencies {
  canDelete: boolean;
  total: number;
  errorCode: string | null;
  dependents: {
    notifications: Array<{ _id: string; name?: string }>;
  };
}

/** Re-scope preview: which projects are aligned to an error master. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, { notifications: number }>;
  references: Record<string, { notifications: number }>;
}

export const errorMasterService = {
  // Get all error masters with pagination and filters.
  // `app_id` (an accessible project id, intersected server-side) is passed through
  // when present even though it is not on ErrorMasterSearchParams (types are isolated).
  getAll: async (params?: ErrorMasterSearchParams & { app_id?: string; include_global?: boolean }): Promise<ErrorMasterSearchResponse> => {
    const response = await api.get<ErrorMasterSearchResponse>('/api/v1/error-master/list', { params });
    return response.data;
  },

  // Preview re-scoping an error master to a project (platform admin only).
  getRescopePreview: async (id: string, appId?: string | null): Promise<RescopePreview> => {
    const response = await api.get<{ data: RescopePreview }>(
      `/api/v1/error-master/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change an error master's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<ErrorMaster> => {
    const response = await api.patch<{ data: ErrorMaster }>(
      `/api/v1/error-master/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  // Get error master by ID
  getById: async (id: string): Promise<ErrorMaster> => {
    const response = await api.get<{ data: ErrorMaster }>(`/api/v1/error-master/get/${id}`);
    return response.data.data;
  },

  // Get error master by error code
  getByCode: async (errorCode: string): Promise<ErrorMaster> => {
    const response = await api.get<ErrorMaster>(`/api/v1/error-master/get/by-code/${errorCode}`);
    return response.data;
  },

  // Create new error master
  create: async (data: ErrorMasterCreateRequest): Promise<ErrorMaster> => {
    const response = await api.post<ErrorMaster>('/api/v1/error-master/create', data);
    return response.data;
  },

  // Update error master
  update: async (id: string, data: ErrorMasterUpdateRequest): Promise<ErrorMaster> => {
    const response = await api.patch<ErrorMaster>(`/api/v1/error-master/update/${id}`, data);
    return response.data;
  },

  // Preview what blocks deletion (notifications referencing this error code)
  getDependencies: async (id: string): Promise<ErrorMasterDependencies> => {
    const response = await api.get<{ data: ErrorMasterDependencies }>(
      `/api/v1/error-master/dependencies/${id}`
    );
    return response.data.data;
  },

  // Delete error master
  delete: async (id: string, restrict: boolean = true): Promise<void> => {
    await api.delete(`/api/v1/error-master/delete/${id}`, {
      params: { restrict },
    });
  },

};